import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button, LoadingBlock, Modal, toast } from "@/components/ds";

/**
 * The Master Admin's editor for the words a rep reads before payment.
 *
 * Saving publishes a new version rather than overwriting: an acknowledgement is only evidence if
 * the exact text the customer agreed to can still be produced afterwards.
 *
 * The placeholders in braces are filled in with that customer's own figures when a rep opens it.
 */
const PLACEHOLDERS = ["{plan_name}", "{business_name}", "{subscription}", "{setup}", "{due_today}"];

export function DisclosureEditor({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const [interval, setInterval] = useState<"monthly" | "annual">("annual");
  const [text, setText] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["payment-disclosures"], queryFn: () => api.disclosuresList(), enabled: open,
  });

  const current = data?.disclosures?.find((d) => d.interval === interval);
  useEffect(() => { setText(current?.text ?? ""); }, [current?.id, current?.text]);

  const saveMut = useMutation({
    mutationFn: () => api.disclosureUpdate(interval, text),
    onSuccess: (res: any) => {
      toast.success(`Saved as version ${res.disclosure.version}.`);
      qc.invalidateQueries({ queryKey: ["payment-disclosures"] });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const unchanged = (current?.text ?? "") === text;

  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Pre-payment disclosure"
      description="What a rep reads to a customer before taking payment." testid="disclosure-editor">
      {isLoading || !data ? <LoadingBlock rows={5} /> : (
        <div className="space-y-4">
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
            {(["monthly", "annual"] as const).map((v) => (
              <button key={v} onClick={() => setInterval(v)} data-testid={`disclosure-tab-${v}`}
                className={`rounded-md px-3 py-1 text-sm capitalize transition-colors ${
                  interval === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                {v}
              </button>
            ))}
          </div>

          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={14}
            data-testid="disclosure-editor-text"
            className="w-full rounded-lg border border-slate-200 p-3 font-mono text-xs leading-relaxed text-slate-800 focus:border-blue-500 focus:outline-none" />

          <p className="text-xs text-slate-500">
            Version {current?.version ?? 1} is in force. Saving publishes a new version; earlier versions are kept,
            because a customer&apos;s agreement refers to the words they were actually read.
          </p>
          <p className="text-xs text-slate-400">
            These are replaced with the customer&apos;s own figures: {PLACEHOLDERS.join("  ")}
          </p>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            <Button onClick={() => saveMut.mutate()} disabled={unchanged || saveMut.isPending || !text.trim()}
              data-testid="disclosure-editor-save">
              {saveMut.isPending ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Saving…</> : "Publish new version"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
